import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Assets,
  ButtonDS,
  MessageTooltip,
  NotInterestedOutlined,
  SidePanelDS,
  Spacer,
  TableTagCard,
  TextCapitalized,
  triggerToast,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import {
  cardsSlice,
  getShipmentLinkAsync,
  selectCardUpdatedStatus,
  selectCardsList,
  selectGetShipmentLink,
  selectRuleUpdated,
} from "../cardsSlice";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { SetCardInOppositionModal } from "../../modals/cards/inOppositionModal";
import "../../../../../i18n/i18n";
import { InformationsCard } from "./informationsCardsSection";
import { ParametersSectionsCard } from "./parametersSectionCard";
import { AmountLimitSectionsCard } from "./amountLimitSectionCard";
import { QrCodeInfo } from "../cardsAPI";

type CardSidePanelProps = {
  canDeactivate?: boolean;
  canUpdateRule?: boolean;
  canRedirectToRule?: boolean;
  lockCardAction: (isCardLocked: boolean, cardUuid: string) => void;
  getQrCodePresignedUrlAction: (qrCode: QrCodeInfo) => void;
};

export const CardSidePanel: FunctionComponent<CardSidePanelProps> = (
  props: CardSidePanelProps,
) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const params = useParams();
  const cardsList = useAppSelector(selectCardsList);
  const card = cardsList.find((c) => c.uuid === params.id);
  const cardUpdatedStatus = useAppSelector(selectCardUpdatedStatus);
  const ruleNewStatus = useAppSelector(selectRuleUpdated);
  const firstPath = location.pathname.split("/")[1]; // home or departments
  const getShipmentLink = useAppSelector(selectGetShipmentLink);

  const [isInOppositionModalVisible, setIsInOppositionModalVisible] =
    useState(false);

  const isCardLocked = useMemo(() => {
    return card?.status === "LOCKED";
  }, [card?.status]);

  useEffect(() => {
    if (!card) {
      navigate(`/${firstPath}/cards/cards`);
    }
  }, [navigate, card, firstPath]);

  useEffect(() => {
    if (card?.shipment && !card.shipmentLink) {
      void dispatch(
        getShipmentLinkAsync({
          shipmentUuid: card.shipment,
          cardUuid: card.uuid,
        }),
      );
    }
  }, [card, getShipmentLink, dispatch]);

  const onSidePanelClosed = useCallback(() => {
    navigate(`/${firstPath}/cards/cards`);
  }, [navigate, firstPath]);

  useEffect(() => {
    if (cardUpdatedStatus === "success" && ruleNewStatus) {
      triggerToast(
        t(`cards.updatedStatus.${ruleNewStatus}.success`) || "",
        "valid",
      );
      dispatch(cardsSlice.actions.resetCardUpdatedStatus());
    } else if (cardUpdatedStatus === "failed") {
      triggerToast(t("cards.updatedStatus.failure") || "", "error");
      dispatch(cardsSlice.actions.resetCardUpdatedStatus());
    }
  }, [ruleNewStatus, cardUpdatedStatus, t, dispatch]);

  const isInOpposition = useMemo(
    () => (card && card.status === "IN_OPPOSITION") ?? false,
    [card],
  );

  if (!card) {
    return;
  }

  return (
    <SidePanelDS.SidePanel
      visible={!!card}
      onClose={onSidePanelClosed}
      absoluteSection={
        props.canDeactivate ? (
          <ButtonDS
            leftIcon={
              <StyledIconColor $isInOpposition={isInOpposition}>
                <NotInterestedOutlined />
              </StyledIconColor>
            }
            text={t("cards.sidePanel.opposition")}
            format={"fill"}
            buttonType={"primary"}
            onClick={() =>
              !isInOpposition &&
              setIsInOppositionModalVisible(!isInOppositionModalVisible)
            }
            iconColor={"errorNormal"}
            disabled={isInOpposition}
          />
        ) : undefined
      }
    >
      <SidePanelDS.Section
        verticalSpacing={1.5}
        background={colors["colors/button/secondary/hover"]}
      >
        <StyledPanelContainer>
          <TextCapitalized>
            {t("cards.sidePanel.number")} **** {card.last4Digits ?? ""}
          </TextCapitalized>
          <Spacer y={1.5} />
          <TableTagCard status={t(`cards.status.${card.status}`)} />
          <Spacer y={1} />
          {isInOpposition ? (
            <>
              <StyledAssets>
                <Assets.UnlockedQiviaCard />
              </StyledAssets>
              <Spacer y={1} />
              <MessageTooltip
                title={t("cards.updatedStatus.IN_OPPOSITION.success")}
                text={t("cards.updatedStatus.IN_OPPOSITION.text")}
                type={"ERROR"}
              />
            </>
          ) : (
            <InformationsCard
              canRedirectToRule={props.canRedirectToRule ?? false}
              firstPath={firstPath}
              isCardLocked={isCardLocked ?? false}
              lockCard={(isCardLocked: boolean, cardUuid: string) =>
                props.lockCardAction(isCardLocked, cardUuid)
              }
              vehicle={{
                name: card.registrationNumber,
                uuid: card.vehicleUuid,
                reference: card.vehicleReference,
                type: card.vehicleType,
                model: [card.vehicleBrand, card.vehicleModel]
                  .filter((d) => d)
                  .join(" "),
              }}
              rule={{
                name: card.ruleName,
                uuid: card.ruleUuid,
              }}
              driver={{
                name: card.driverFirstName ?? "",
                lastName: card.driverLastName ?? "",
                uuid: card.driverUuid,
              }}
              cardUuid={card.uuid}
              qrCodes={card.qrCodes}
              getQrCodePresignedUrlAction={props.getQrCodePresignedUrlAction}
              shipmentLink={card.shipmentLink ?? ""}
            />
          )}
        </StyledPanelContainer>
      </SidePanelDS.Section>
      <AmountLimitSectionsCard
        isInOpposition={isInOpposition}
        monthlyAmountlimit={card.monthlyAmountlimit}
        monthlyExpenses={card.monthlyExpenses}
      />
      {props.canUpdateRule ? (
        <ParametersSectionsCard
          vehicleUuid={card.vehicleUuid}
          isInOpposition={isInOpposition}
          currentRule={card.ruleUuid}
        />
      ) : (
        <></>
      )}
      <SetCardInOppositionModal
        isVisible={isInOppositionModalVisible}
        onCloseModal={() => {
          setIsInOppositionModalVisible(false);
        }}
        cardUuid={card.uuid}
      />
    </SidePanelDS.SidePanel>
  );
};

const StyledPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const StyledAssets = styled.div`
  display: flex;
  justify-content: center;
  & svg {
    width: 13rem;
    height: 9.5rem;
  }
`;

const StyledIconColor = styled.div<{ $isInOpposition: boolean }>`
  & svg {
    fill: ${({ $isInOpposition }) => ($isInOpposition ? "grey" : "")};
  }
`;
